import { useCallback, useEffect, useRef, useState } from 'react';

const RECONNECT_DELAY_MS = 3000;

const getStoredUserId = () => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return user.userId || user.user_id || user.id || null;
  } catch {
    return null;
  }
};

const buildNotificationSocketUrl = (userId) => {
  const baseUrl = process.env.REACT_APP_BASE_URL || 'http://3.139.107.45:8001';
  const isSecure = baseUrl.startsWith('https://');
  const wsProtocol = isSecure ? 'wss' : 'ws';
  const cleanBase = baseUrl.replace(/^https?:\/\//, '').replace(/\/+$/, '');
  return `${wsProtocol}://${cleanBase}/ws/notifications/${userId}`;
};

export const DISCOVERY_PROGRESS_EVENT = 'discovery_progress';
export const EXECUTION_PROGRESS_EVENT = 'execution_progress';

// The progress object can arrive nested under `message`, `message.data` or at
// the root, depending on how the backend wraps the notification.
export const extractDiscoveryProgress = (payload, messageType = DISCOVERY_PROGRESS_EVENT) => {
  if (!payload || typeof payload !== 'object') return null;

  const candidates = [payload?.message?.data, payload?.message, payload?.data, payload];
  const match = candidates.find(
    (candidate) => candidate && typeof candidate === 'object' && candidate.type === messageType
  );

  return match || null;
};

const isTerminalRunStatus = (status) => {
  const value = String(status || '').toLowerCase();
  return ['completed', 'complete', 'success', 'succeeded', 'finished', 'done', 'failed', 'fail', 'error', 'cancelled', 'canceled'].includes(value);
};

/**
 * Subscribes to progress events on ws/notifications/<userId>.
 *
 * @param {object} options
 * @param {number|string} [options.userId]      Defaults to the logged in user.
 * @param {number|string} [options.runId]       When set, payloads carrying a
 *        different `run_id` are ignored.
 * @param {boolean} [options.enabled=true]      Connect only while true.
 * @param {string}  [options.messageType]       Event type to listen for:
 *        `discovery_progress` (crawl) or `execution_progress` (test run).
 */
export default function useDiscoveryProgressSocket({
  userId,
  runId,
  enabled = true,
  messageType = DISCOVERY_PROGRESS_EVENT
} = {}) {
  const [progress, setProgress] = useState(null);
  const [status, setStatus] = useState('disconnected');
  const socketRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const terminalRef = useRef(false);

  const resolvedUserId = userId || getStoredUserId();

  const clearReconnectTimer = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  const closeSocket = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.onopen = null;
      socketRef.current.onmessage = null;
      socketRef.current.onerror = null;
      socketRef.current.onclose = null;
      if (
        socketRef.current.readyState === WebSocket.OPEN ||
        socketRef.current.readyState === WebSocket.CONNECTING
      ) {
        socketRef.current.close(1000, 'Discovery socket closed');
      }
      socketRef.current = null;
    }
  }, []);

  useEffect(() => {
    terminalRef.current = false;
    setProgress(null);

    if (!enabled || !resolvedUserId) {
      setStatus('disconnected');
      return undefined;
    }

    let disposed = false;

    const connect = () => {
      if (disposed || terminalRef.current) return;

      closeSocket();
      setStatus('connecting');

      const wsUrl = buildNotificationSocketUrl(resolvedUserId);
      const socket = new WebSocket(wsUrl);
      socketRef.current = socket;

      socket.onopen = () => {
        if (disposed) return;
        setStatus('connected');
      };

      socket.onmessage = (event) => {
        if (disposed) return;

        try {
          const payload = JSON.parse(event.data);
          const discoveryProgress = extractDiscoveryProgress(payload, messageType);
          if (!discoveryProgress) return;

          if (runId && discoveryProgress.run_id && String(discoveryProgress.run_id) !== String(runId)) {
            return;
          }

          setProgress(discoveryProgress);

          if (isTerminalRunStatus(discoveryProgress.run_status)) {
            terminalRef.current = true;
            clearReconnectTimer();
            closeSocket();
            setStatus('disconnected');
          }
        } catch (error) {
          console.warn('[Progress WS] Failed to parse message:', event.data, error);
        }
      };

      socket.onerror = (error) => {
        console.error('[Progress WS] Error:', error);
        if (!disposed) setStatus('error');
      };

      socket.onclose = (event) => {
        if (disposed || terminalRef.current || event.code === 1000) {
          if (!disposed && !terminalRef.current) setStatus('disconnected');
          return;
        }

        setStatus('disconnected');
        clearReconnectTimer();
        reconnectTimerRef.current = setTimeout(connect, RECONNECT_DELAY_MS);
      };
    };

    connect();

    return () => {
      disposed = true;
      clearReconnectTimer();
      closeSocket();
      setStatus('disconnected');
    };
  }, [enabled, resolvedUserId, runId, messageType, closeSocket, clearReconnectTimer]);

  return {
    progress,
    status,
    userId: resolvedUserId
  };
}
